import { useState } from "react";
import { Camera, Save, Sparkles, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Modal, modalBtnPrimary, modalBtnSecondary } from "../Modal";
import { Field, inputCls } from "./TripForm";

export function FuelingForm({ editing, setEditing, onSaved, onClose, vehicles, money }) {
    const [photo, setPhoto] = useState(null);
    const [busy, setBusy] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [scan, setScan] = useState(null);
    const showAmount = money || !editing.id;  // částku zadá řidič při zápisu; po uložení ji technik/řemeslník nevidí
    const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const onPhoto = async (file) => {
        setPhoto(file || null);
        if (!file || !file.type.startsWith("image/")) return;
        setScanning(true); setScan(null);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/fleet/fuelings/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
            setScan(data);
            setEditing((prev) => ({
                ...prev,
                datetime: data.datetime || prev.datetime,
                liters: data.liters ?? prev.liters,
                amount: showAmount && data.amount != null && data.currency !== "EUR" ? data.amount : prev.amount,
                odometer: data.odometer ?? prev.odometer,
                note: prev.note || data.station || "",
            }));
            toast.success(data.liters ? `AI vyčetla ${data.liters} l${data.amount != null ? ` · ${data.amount} ${data.currency || "Kč"}` : ""}` : "AI účtenku přečetla, zkontrolujte údaje");
        } catch (e) { toast.error(e.response?.data?.detail || "AI nedokázala účtenku přečíst – vyplňte údaje ručně"); }
        finally { setScanning(false); }
    };

    const save = async () => {
        if (!editing.vehicle_id) { toast.error("Vyberte vozidlo"); return; }
        if (!(Number(editing.liters) > 0)) { toast.error("Zadejte litry"); return; }
        const payload = { vehicle_id: editing.vehicle_id, datetime: editing.datetime, odometer: editing.odometer === "" || editing.odometer == null ? null : Number(editing.odometer),
                          liters: Number(editing.liters), full_tank: !!editing.full_tank, note: editing.note || "",
                          amount: money && editing.amount !== "" && editing.amount != null ? Number(editing.amount) : null };
        setBusy(true);
        try {
            const { data } = editing.id ? await api.put(`/fleet/fuelings/${editing.id}`, payload) : await api.post("/fleet/fuelings", payload);
            if (photo) {
                const fd = new FormData(); fd.append("file", photo);
                await api.post(`/fleet/fuelings/${data.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success("Tankování uloženo"); onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setBusy(false); }
    };

    return (
        <Modal title={editing.id ? "Upravit tankování" : "Tankování"} onClose={onClose} testid="fueling-modal"
               footer={<>
                   <button onClick={onClose} className={modalBtnSecondary}>Zrušit</button>
                   <button onClick={save} disabled={busy} className={modalBtnPrimary} data-testid="save-fueling"><Save className="w-3.5 h-3.5" /> {busy ? "Ukládám…" : "Uložit"}</button>
               </>}>
            <div className="space-y-4">
                <div>
                    <label className="label-eyebrow block mb-1.5">Vozidlo *</label>
                    <select value={editing.vehicle_id || ""} onChange={set("vehicle_id")} className={inputCls} data-testid="fuel-vehicle">
                        <option value="">— vyberte vozidlo —</option>
                        {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}{v.plate ? ` (${v.plate})` : ""}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Datum a čas *" type="datetime-local" v={editing.datetime} on={set("datetime")} testid="fuel-datetime" />
                    <Field label="Stav tachometru" type="number" inputMode="numeric" v={editing.odometer} on={set("odometer")} testid="fuel-odometer" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Litry *" type="number" inputMode="decimal" step="0.01" v={editing.liters} on={set("liters")} testid="fuel-liters" />
                    {showAmount && <Field label="Částka Kč (z účtenky)" type="number" inputMode="decimal" step="0.01" v={editing.amount} on={set("amount")} testid="fuel-amount" />}
                </div>
                <label className="flex items-center gap-3 min-h-[44px] text-sm" data-testid="fuel-full-tank">
                    <input type="checkbox" checked={!!editing.full_tank} onChange={set("full_tank")} className="w-5 h-5 accent-orange-600" /> Plná nádrž
                </label>
                <div>
                    <label className="label-eyebrow block mb-1.5">Foto účtenky <span className="normal-case tracking-normal font-normal text-slate-400">· AI předvyplní litry, částku a datum</span></label>
                    <label className={`flex items-center gap-2 min-h-[44px] px-3 border border-dashed rounded-sm text-sm cursor-pointer hover:bg-slate-50 ${scanning ? "border-orange-500 bg-orange-50" : "border-slate-400"}`} data-testid="fuel-photo-label">
                        {scanning ? <Loader2 className="w-4 h-4 text-orange-600 animate-spin" /> : <Camera className="w-4 h-4 text-orange-600" />}
                        {scanning ? "AI čte účtenku…" : photo ? photo.name : editing.photo_name ? `Uloženo: ${editing.photo_name} (nahrát nové)` : "Vyfotit / vybrat účtenku"}
                        <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => onPhoto(e.target.files?.[0])} disabled={scanning} data-testid="fuel-photo" />
                    </label>
                    {scan && (
                        <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-1.5 rounded-sm" data-testid="fuel-scan-result">
                            <Sparkles className="w-3 h-3 mt-0.5 shrink-0" />
                            <span>Z účtenky: {[scan.station, scan.datetime?.replace("T", " "), scan.liters != null && `${scan.liters} l`, scan.amount != null && `${scan.amount} ${scan.currency || "Kč"}`, scan.price_per_liter != null && `${scan.price_per_liter} Kč/l`].filter(Boolean).join(" · ")}. Zkontrolujte a případně opravte.{scan.currency === "EUR" && " Částka je v EUR – přepočítejte na Kč ručně."}</span>
                        </div>
                    )}
                </div>
                <Field label="Poznámka" v={editing.note} on={set("note")} testid="fuel-note" />
            </div>
        </Modal>
    );
}
